import React, { useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import { MapPin, Navigation, Clock } from 'react-feather';
import L from 'leaflet';
import { Accident } from '../types';
import './MapView.css';

// Fix for default markers in React Leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

interface MapViewProps {
  accidents: Accident[];
  selectedAccident?: Accident | null;
  onClearSelection?: () => void;
  enabledSources?: string[];
  zoneBounds?: { north: number; south: number; east: number; west: number } | null;
}

// Your Mapbox token
const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN || '';

// Custom icons for different severity levels with source letters
const createCustomIcon = (accident: Accident, isSelected: boolean = false) => {
  const colors = {
    low: '#28a745',
    medium: '#ffc107', 
    high: '#fd7e14',
    critical: '#dc3545'
  };

  const sourceLetters = {
    waze: 'W',
    'transport-nsw': 'T',
    psn: 'P'
  };

  const size = isSelected ? 32 : 24;
  const borderWidth = isSelected ? 4 : 3;
  const borderColor = isSelected ? '#667eea' : 'white';
  const sourceLetter = sourceLetters[accident.source];
  
  // Use alert triangle icon for Waze accidents, letters for others
  const iconSize = Math.round(size * 0.55);
  const markerContent = accident.source === 'waze' 
    ? `<svg xmlns="http://www.w3.org/2000/svg" width="${iconSize}" height="${iconSize}" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path><line x1="12" y1="9" x2="12" y2="13"></line><line x1="12" y1="17" x2="12.01" y2="17"></line></svg>`
    : sourceLetter;

  return L.divIcon({
    className: 'custom-div-icon',
    html: `
      <div style="
        background-color: ${colors[accident.severity]};
        width: ${size}px;
        height: ${size}px;
        border-radius: 50%;
        border: ${borderWidth}px solid ${borderColor};
        box-shadow: 0 4px 12px rgba(0,0,0,0.4);
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: ${isSelected ? '14px' : '12px'};
        color: white;
        font-weight: bold;
        cursor: pointer;
        transition: transform 0.2s ease;
        ${isSelected ? 'animation: pulse-selected 2s infinite;' : ''}
      ">${markerContent}</div>
    `,
    iconSize: [size + borderWidth * 2, size + borderWidth * 2],
    iconAnchor: [(size + borderWidth * 2) / 2, (size + borderWidth * 2) / 2]
  });
};

const FitBounds: React.FC<{ accidents: Accident[], selectedAccident?: Accident | null }> = ({ accidents, selectedAccident }) => {
  const map = useMap();

  useEffect(() => {
    // If there's a selected accident, zoom to it
    if (selectedAccident) {
      map.setView([selectedAccident.location.lat, selectedAccident.location.lng], 15);
      return;
    }

    if (accidents.length > 0) {
      const bounds = L.latLngBounds(accidents.map(acc => [acc.location.lat, acc.location.lng]));
      
      // Fit bounds but don't zoom out too far (max zoom out is level 11)
      // and don't zoom in too close (max zoom in is level 15)
      map.fitBounds(bounds, { 
        padding: [50, 50],
        maxZoom: 14,  // Don't zoom in too close
      });
      
      // If zoomed out too far, set a minimum zoom level
      if (map.getZoom() < 10) {
        map.setZoom(10);
      }
    }
  }, [map, accidents, selectedAccident]);

  return null;
};

export const MapView: React.FC<MapViewProps> = ({ accidents, selectedAccident, onClearSelection, enabledSources = ['waze', 'transport-nsw', 'psn'], zoneBounds }) => {
  // Calculate center based on available data
  // Priority: selected accident > first accident > zone center > Sydney fallback
  const getMapCenter = (): [number, number] => {
    if (selectedAccident) {
      return [selectedAccident.location.lat, selectedAccident.location.lng];
    }
    if (accidents.length > 0) {
      // Center on first accident if no selection
      return [accidents[0].location.lat, accidents[0].location.lng];
    }
    // If we have zone bounds, center on the zone
    if (zoneBounds) {
      const centerLat = (zoneBounds.north + zoneBounds.south) / 2;
      const centerLng = (zoneBounds.east + zoneBounds.west) / 2;
      return [centerLat, centerLng];
    }
    // Fallback to Sydney CBD if no data and no zones
    return [-33.8688, 151.2093]; // Sydney CBD
  };
  
  const mapCenter = getMapCenter();
  const mapZoom = selectedAccident ? 15 : accidents.length > 0 ? 13 : zoneBounds ? 12 : 10;

  const formatTimeAgo = (timestamp: Date) => {
    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - timestamp.getTime()) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours}h ago`;
    
    const diffInDays = Math.floor(diffInHours / 24);
    return `${diffInDays}d ago`;
  };

  const formatEstimatedClearTime = (clearTime?: Date) => {
    if (!clearTime) return null;
    
    const now = new Date();
    const diffInMinutes = Math.floor((clearTime.getTime() - now.getTime()) / (1000 * 60));
    
    if (diffInMinutes <= 0) return 'Clearing soon';
    if (diffInMinutes < 60) return `~${diffInMinutes}m to clear`;
    
    const diffInHours = Math.floor(diffInMinutes / 60);
    const remainingMinutes = diffInMinutes % 60;
    
    if (remainingMinutes === 0) return `~${diffInHours}h to clear`;
    return `~${diffInHours}h ${remainingMinutes}m to clear`;
  };

  return (
    <div className="main-map-view">
      <div className="map-controls">
        <div className="live-accidents-counter">
          <span className="counter-number">{accidents.length}</span>
          <span className="counter-label">Live Incidents</span>
        </div>
        {selectedAccident && onClearSelection && (
          <button 
            className="selected-accident-info"
            onClick={onClearSelection}
            title="Click to clear selection"
          >
            <div className="selected-info">
              <MapPin size={14} style={{ marginRight: '6px', display: 'inline' }} />
              <span className="selected-title">{selectedAccident.title}</span>
              <span className="selected-subtitle">Click to clear selection</span>
            </div>
          </button>
        )}
      </div>

      <MapContainer
        center={mapCenter}
        zoom={mapZoom}
        className="map-container"
        zoomControl={false}
        attributionControl={false}
        key={selectedAccident ? selectedAccident.id : 'default'}
      >
        {/* Use Mapbox Streets tiles with your token */}
        <TileLayer
          attribution='© <a href="https://www.mapbox.com/about/maps/">Mapbox</a> © <a href="http://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url={`https://api.mapbox.com/styles/v1/mapbox/streets-v12/tiles/{z}/{x}/{y}?access_token=${MAPBOX_TOKEN}`}
        />
        
        {accidents
          .filter(accident => enabledSources.includes(accident.source))
          .map((accident) => {
          const isSelected = selectedAccident?.id === accident.id;
          return (
            <Marker
              key={accident.id}
              position={[accident.location.lat, accident.location.lng]}
              icon={createCustomIcon(accident, isSelected)}
            >
            <Popup className="accident-popup" maxWidth={300}>
              <div className="popup-content">
                <div className="popup-header">
                  <h4>{accident.title}</h4>
                  <span className={`severity-indicator severity-${accident.severity}`}>
                    {accident.severity.toUpperCase()}
                  </span>
                </div>
                <p className="popup-description">{accident.description}</p>
                <div className="popup-details">
                  <div className="popup-detail">
                    <MapPin size={14} style={{ marginRight: '6px', display: 'inline' }} />
                    <strong>Location:</strong> {accident.location.address}
                  </div>
                  <div className="popup-detail">
                    <Navigation size={14} style={{ marginRight: '6px', display: 'inline' }} />
                    <strong>Lanes affected:</strong> {accident.affectedLanes}
                  </div>
                  <div className="popup-detail">
                    <Clock size={14} style={{ marginRight: '6px', display: 'inline' }} />
                    <strong>Reported:</strong> {formatTimeAgo(accident.timestamp)}
                  </div>
                  {accident.estimatedClearTime && (
                    <div className="popup-detail">
                      <Clock size={14} style={{ marginRight: '6px', display: 'inline' }} />
                      <strong>Est. clear time:</strong> {formatEstimatedClearTime(accident.estimatedClearTime)}
                    </div>
                  )}
                </div>
                <div className="popup-actions">
                  <button className="btn btn-primary popup-btn">
                    <Navigation size={14} style={{ marginRight: '6px' }} />
                    Directions
                  </button>
                </div>
              </div>
                         </Popup>
           </Marker>
         );
        })}
         
         <FitBounds accidents={accidents} selectedAccident={selectedAccident} />
      </MapContainer>
    </div>
  );
}; 